import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { client } from 'apolloClient/client';
import { UPDATE_ME } from 'apolloClient/mutations/me';
import { GET_ME } from 'apolloClient/queries/me';
import { Response } from 'apolloClient/types/common';
import { FavoriteListingsResponse } from 'apolloClient/types/FavoriteListing';
import { FavoriteBuildingsResponse } from 'apolloClient/types/FavoriteBuilding';
import { SavedSearchesResponse } from 'apolloClient/types/Searches';
import { flattenStrapiDataItem } from 'lib/flattenStrapiBulkDataItems';
import {
  AuthenticatedUserInfo,
  AuthenticatedUserState,
} from '../../apolloClient/types';
import { initialAuthData } from '../../src/constants/authConstants';
import {
  getAuthDataFromCookies,
  getAuthDataFromSessionStorage,
  updateAuthDataInCookies,
} from '../../src/utils/authUtils';
import {
  StrapiAgent,
  StrapiAgentResponse,
} from '../../apolloClient/types/Agents';
import { VERIFY_EMAIL } from 'apolloClient/mutations/auth';
import { EXCHANGE_EMAIL_TOKEN } from 'apolloClient/mutations/exchangeEmailToken';
import { useRouter } from 'next/router';
import { removeQueryParams, saveLeadIdToLocalStorage } from 'src/utils/utils';

type TNOOP = () => void;

interface AuthContextInterface {
  authData: AuthenticatedUserInfo;
  onLoggedIn: null | ((info: AuthenticatedUserInfo) => void);
  me?: null | AuthenticatedUserState;
  agent: StrapiAgent | null;
  logout: TNOOP;
  updateMe: (user: Partial<AuthenticatedUserState>) => Promise<void>;
  isLogged: boolean;
  headerPopupOpen: boolean;
  openHeaderPopup: (params?: {
    successHandler?: TNOOP;
    finnallyHandler?: TNOOP;
    keepOpen?: boolean;
  }) => void;
  closeHeaderPopup: TNOOP;
  onSuccess: TNOOP;
  onFinnaly: TNOOP;
  keepOpen: boolean;
  setMe: (me: AuthenticatedUserState) => void;
  searchesCount: number;
  favoritesCount: number;
  favoritesBuildingsCount: number;
  getActualAuthData: () => AuthenticatedUserInfo;
  isLoginTriggered: boolean;
  isAuthProcessed: boolean;
}

const NOOP = () => {};

const getAuthData = () => {
  if (typeof window === 'object') {
    const sessionStorageData = getAuthDataFromSessionStorage();
    if (sessionStorageData.jwt) {
      return sessionStorageData;
    }

    return getAuthDataFromCookies(document.cookie);
  } else {
    return initialAuthData;
  }
};

export const AuthContext = createContext<AuthContextInterface>({
  agent: null,
  authData: initialAuthData,
  onLoggedIn: null,
  logout: NOOP,
  updateMe: async () => {},
  isLogged: false,
  headerPopupOpen: false,
  openHeaderPopup: NOOP,
  closeHeaderPopup: NOOP,
  onSuccess: NOOP,
  onFinnaly: NOOP,
  keepOpen: false,
  setMe: NOOP,
  searchesCount: 0,
  favoritesCount: 0,
  getActualAuthData: () => getAuthData(),
  isLoginTriggered: false,
  isAuthProcessed: false,
  favoritesBuildingsCount: 0,
});

interface Props {
  authDataFromCookie: AuthenticatedUserInfo;
  children?: React.ReactNode;
}

const AuthProvider: React.FC<Props> = ({ children, authDataFromCookie }) => {
  const [authData, setAuthData] =
    useState<AuthenticatedUserInfo>(authDataFromCookie);

  const [isLoginTriggered, setLoginTriggered] = useState(false);

  const [me, setMe] = useState<AuthenticatedUserState | null>();
  const [agent, setAgent] = useState<StrapiAgent | null>(null);

  const [isKeepOpen, setKeepOpen] = useState(false);
  const [isAuthProcessed, setIsAuthProcessed] = useState(false);

  const [favoritesCount, setFavoritesCount] = useState(0);
  const [favoritesBuildingsCount, setFavoritesBuildingsCount] = useState(0);
  const [searchesCount, setSearchesCount] = useState(0);
  const router = useRouter();

  const [isEmailTokenExchanging, setIsEmailTokenExchanging] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).has('token');
  });

  useEffect(() => {
    if (!router.isReady) return;

    const { token } = router.query;
    if (!token || typeof token !== 'string') {
      setIsEmailTokenExchanging(false);
      return;
    }

    let mounted = true;
    const { token: _t, ...rest } = router.query;
    const { pathname } = router;

    client
      .mutate<{ exchangeEmailToken: string }, { token: string }>({
        mutation: EXCHANGE_EMAIL_TOKEN,
        variables: { token },
      })
      .then(({ data }) => {
        if (!mounted) return;
        if (data?.exchangeEmailToken) {
          const jwt = data.exchangeEmailToken;
          try {
            const payload = JSON.parse(atob(jwt.split('.')[1]));
            const newAuthData = { jwt, user: { id: payload.id } as any };
            updateAuthDataInCookies(newAuthData);
            setAuthData(newAuthData);
          } catch (e) {
            console.warn('[email-token] malformed JWT payload:', e);
          }
          router.replace({ pathname, query: rest }, undefined, {
            shallow: true,
          });
        }
      })
      .catch((err) => {
        if (!mounted) return;
        console.warn('[email-token] exchange failed:', err.message);
        router.replace({ pathname, query: rest });
      })
      .finally(() => {
        if (mounted) setIsEmailTokenExchanging(false);
      });

    return () => { mounted = false; };
  }, [router.isReady]);

  useEffect(() => {
    if (router.query.login) {
      setHeaderPopupOpen(true);
      removeQueryParams(['login'], router);
    }
  }, [router]);

  useEffect(() => {
    if (isEmailTokenExchanging) return;

    if (!authData?.user?.id) {
      setFavoritesCount(0);
      setFavoritesBuildingsCount(0);
      setSearchesCount(0);
      setMe(null);
      setAgent(null);
      setIsAuthProcessed(true);
      return;
    }

    const refetchMe = async () => {
      const {
        data: { me, favoriteListings, favoriteBuildings, savedSearches, agent },
      } = await client.query<{
        me: AuthenticatedUserState;
        favoriteListings: Pick<FavoriteListingsResponse, 'meta'>;
        favoriteBuildings: Pick<FavoriteBuildingsResponse, 'meta'>;
        savedSearches: Pick<SavedSearchesResponse, 'meta'>;
        agent: StrapiAgentResponse;
      }>({
        query: GET_ME,
      });
      setMe({ ...me, isAdmin: authData?.user?.isAdmin || me?.isAdmin });
      if (me.leadId) saveLeadIdToLocalStorage(me.leadId);
      agent?.data?.id && setAgent(flattenStrapiDataItem(agent.data));
      savedSearches?.meta?.pagination?.total &&
        setSearchesCount(savedSearches.meta.pagination.total);
      favoriteListings?.meta?.pagination?.total &&
        setFavoritesCount(favoriteListings?.meta?.pagination?.total);
      favoriteBuildings?.meta?.pagination?.total &&
        setFavoritesBuildingsCount(favoriteBuildings?.meta?.pagination?.total);
      setIsAuthProcessed(true);
    };
    refetchMe();
  }, [authData?.user?.id, isEmailTokenExchanging]);

  const updateMe = async (updateFields: Partial<AuthenticatedUserState>) => {
    if (me?.email !== updateFields.email) {
      const {
        data: {
          verifyEmail: { ok: isEmailExists },
        },
      } = await client.query({
        query: VERIFY_EMAIL,
        fetchPolicy: 'no-cache',
        variables: {
          email: updateFields.email,
        },
      });

      if (isEmailExists) throw new Error(`Email already exists`);
    }

    const response = await client.mutate<
      {
        updateUsersPermissionsUser: Response<
          Omit<AuthenticatedUserState, 'id'>
        >;
      },
      { id: number; data: Partial<AuthenticatedUserState> }
    >({
      mutation: UPDATE_ME,
      variables: {
        id: authData.user.id || -1,
        data: updateFields,
      },
    });
    response.data;
    const user = flattenStrapiDataItem(
      response.data?.updateUsersPermissionsUser.data
    );

    setMe(user);
  };

  useEffect(() => {
    setAuthData(authDataFromCookie);
  }, [authDataFromCookie]);

  function logout() {
    setAuthData(initialAuthData);
    setMe(null);
    updateAuthDataInCookies(initialAuthData);
    setSuccessHandler(null);
    setFinnalyHandler(null);
  }

  const [headerPopupOpen, setHeaderPopupOpen] = useState(false);
  const [successHandler, setSuccessHandler] = useState<(() => void) | null>();
  const [finnalyHandler, setFinnalyHandler] = useState<(() => void) | null>();

  const onSuccess = () => {
    successHandler && successHandler();
    setSuccessHandler(null);
    setKeepOpen(false);
    setHeaderPopupOpen(false);
  };

  const onFinnaly = () => {
    finnalyHandler && finnalyHandler();
    setFinnalyHandler(null);
  };

  const onLoggedIn = (info: AuthenticatedUserInfo) => {
    setAuthData(info);
    setLoginTriggered(true);
  };

  const openHeaderPopup = useCallback(
    (params?: {
      successHandler?: TNOOP;
      finnallyHandler?: TNOOP;
      keepOpen?: boolean;
    }) => {
      setHeaderPopupOpen(true);
      if (params?.successHandler)
        setSuccessHandler(() => params.successHandler);
      if (params?.finnallyHandler)
        setFinnalyHandler(() => params.finnallyHandler);
      if (params?.keepOpen) setKeepOpen(true);
    },
    []
  );

  const closeHeaderPopup = useCallback(() => {
    if (isKeepOpen) return;
    setHeaderPopupOpen(false);
    finnalyHandler && finnalyHandler();
  }, [finnalyHandler, isKeepOpen]);

  const getActualAuthData = useCallback(() => getAuthData(), []);

  return (
    <AuthContext.Provider
      value={{
        agent,
        searchesCount,
        favoritesCount,
        favoritesBuildingsCount,
        authData,
        onLoggedIn,
        logout,
        updateMe,
        me,
        isLogged: Boolean(authData?.user?.id),
        headerPopupOpen,
        setMe,
        openHeaderPopup,
        closeHeaderPopup,
        onSuccess,
        onFinnaly,
        keepOpen: isKeepOpen,
        getActualAuthData,
        isLoginTriggered,
        isAuthProcessed,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const useOnLoggedIn = (
  callBack: (authData: AuthenticatedUserInfo) => void
) => {
  const { isLogged, authData } = useAuth();

  useEffect(() => {
    if (isLogged) {
      callBack(authData);
    }
  }, [isLogged]);
};

export default AuthProvider;
